import { Box, Button, Divider, Tooltip, Typography } from '@mui/material';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import { PageHeader } from 'src/components/molecules/page-header';
import Page from 'src/components/organisms/page/page';
import { Pagination } from 'src/components/organisms/pagination';
import { useMeilisearchWithUrlParams } from 'src/hooks/meilisearch/use-meilisearch-with-url-params';
import { useDialog } from 'src/hooks/use-dialog';
import { useSortController } from 'src/hooks/use-sort-controller';
import { Plus } from 'src/icons/plus';
import { Can, withPermissions } from 'src/permissions/can';
import { useUserStore } from 'src/store/user-store';
import { SortOrder } from 'src/types/enum/sort-order';
import { getDateIsoStringWithoutSecondsAndMilliseconds } from 'src/utils/get-date-iso-string-without-seconds-and-milliseconds';
import { HintBox } from '../../../../components/molecules/hint-box';
import { useUnblockSpace } from '../../api/hooks/use-unblock-space';
import { fetchSpaces } from '../../api/lib/spaces';
import { spacesKeys } from '../../api/query-keys/spaces';
import { SpaceStateFilterPopover } from '../molecules/filters/space-state-filter-popover';
import { SpacesFilterChips } from '../molecules/filters/spaces-filter-chips';
import { SpacesActionsTableMenu } from '../molecules/spaces-actions-table-menu';
import { SpacesActionsSlotPayload } from '../molecules/spaces-table-row';
import { BlockSpaceDialog } from '../organisms/dialogs/block-space-dialog/block-space-dialog';
import { DeleteSpaceDialog } from '../organisms/dialogs/delete-space-dialog/delete-space-dialog';
import { UnblockSpaceDialog } from '../organisms/dialogs/unblock-space-dialog/unblock-space-dialog';
import { UploadSpacesDialog } from '../organisms/dialogs/upload-spaces-dialog';
import { SpacesMoreFilters } from '../organisms/spaces-more-filters';
import { SpacesTable } from '../organisms/spaces-table';

const Spaces = () => {
  const { t } = useTranslation(['spaces', 'common']);
  const listViewLabel = t('spaces:common.tabs.listView');
  const mapEditorLabel = t('spaces:common.tabs.mapEditor');
  const overviewHint = t('spaces:common.hints.listVsMap', {
    listViewLabel,
    mapEditorLabel,
  });

  const navigate = useNavigate();

  const { directoryToken } = useUserStore();

  const { query, perPage, page, filter } = useSearch({
    from: '/_layout/tenant/spaces/_layout/',
    select(state) {
      return {
        page: state.page,
        perPage: state.limit,
        query: state.q,
        filter: state.filter,
      };
    },
    structuralSharing: true,
  });

  const [sortController, setSortController] = useSortController({
    sortBy: 'name',
    sortOrder: SortOrder.DESC,
  });

  const defaultSearchStateConfig = useMemo(() => {
    return {
      sort: [`${sortController.sortBy}:${sortController.sortOrder}`],
    };
  }, [sortController.sortBy, sortController.sortOrder]);

  const {
    results,
    handleChangeQuery,
    handleChangePage,
    isLoading,
    isFetching,
    handleChangeLimit,
    error,
    handleChangeFilterValue,
    handleChangeSort,
    resetFilters,
  } = useMeilisearchWithUrlParams(
    fetchSpaces,
    spacesKeys.list(directoryToken ?? '', page, perPage),
    defaultSearchStateConfig,
  );

  const [
    isOpenUploadSpacesDialog,
    handleOpenUploadSpacesDialog,
    handleCloseUploadSpacesDialog,
  ] = useDialog();

  const [
    isOpenDeleteSpacesDialog,
    handleOpenDeleteSpacesDialog,
    handleCloseDeleteSpacesDialog,
  ] = useDialog();

  const [
    isOpenBlockSpaceDialog,
    handleOpenBlockSpaceDialog,
    handleCloseBlockSpaceDialog,
  ] = useDialog();

  const [
    isOpenUnblockSpaceDialog,
    handleOpenUnblockSpaceDialog,
    handleCloseUnblockSpaceDialog,
  ] = useDialog();

  //Actions slot
  const [selectedSpace, setSelectedSpace] =
    useState<SpacesActionsSlotPayload | null>(null);

  const { mutate: unblockSpace } = useUnblockSpace();

  const actionsSlot = useCallback(
    ({
      token,
      name,
      spaceBlockedStates,
      spacePermanentlyBlockedState,
      spaceGroupToken,
    }: SpacesActionsSlotPayload) => (
      <SpacesActionsTableMenu
        onEdit={() => {
          navigate({
            to: `/tenant/spaces/${token}/edit`,
            state: { backUrl: location.href },
          });
        }}
        onBlock={() => {
          setSelectedSpace({
            token,
            name,
            spaceBlockedStates,
            spacePermanentlyBlockedState,
            spaceGroupToken,
          });
          handleOpenBlockSpaceDialog();
        }}
        isPermanentlyBlocked={Boolean(spacePermanentlyBlockedState)}
        hasBlockedState={spaceBlockedStates.length > 0}
        onUnblock={() => {
          if (spacePermanentlyBlockedState) {
            unblockSpace({
              spaceToken: token,
              payload: {
                from: getDateIsoStringWithoutSecondsAndMilliseconds(
                  new Date(spacePermanentlyBlockedState.from),
                ),
                until: null,
              },
            });

            return;
          }

          setSelectedSpace({
            token,
            name,
            spaceBlockedStates,
            spacePermanentlyBlockedState,
            spaceGroupToken,
          });
          handleOpenUnblockSpaceDialog();
        }}
        onDelete={() => {
          //TODO: Check if there are any bookings for this space, then open different dialog
          setSelectedSpace({
            token,
            name,
            spaceBlockedStates,
            spacePermanentlyBlockedState,
            spaceGroupToken,
          });
          handleOpenDeleteSpacesDialog();
        }}
      />
    ),
    [],
  );

  const totalPages = Math.ceil((results?.estimatedTotalHits ?? 0) / perPage);

  return (
    <>
      <PageHeader
        title={t('spaces:common.title')}
        extras={
          <>
            <Typography variant="body2">
              {t('spaces:common.total', {
                total: results?.estimatedTotalHits ?? 0,
              })}
            </Typography>

            <Can I="create" a="Spaces" passThrough>
              {allowed => (
                <Tooltip
                  title={t('common:notSufficientPermissions')}
                  disableHoverListener={allowed}
                  disableFocusListener={allowed}>
                  <span>
                    <Button
                      sx={{ ml: 2 }}
                      onClick={() =>
                        navigate({
                          to: '/tenant/spaces/create',
                          state: { backUrl: location.href },
                        })
                      }
                      color="primary"
                      disabled={!allowed}
                      startIcon={<Plus fontSize="small" />}
                      variant="outlinedContained"
                      size="large">
                      {t('spaces:common.addNewSpace')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Can>

            {/* // TODO: removed - this is moved to new location */}
            {/* <Can I="map" a="Spaces" passThrough>
              {allowed => (
                <Tooltip
                  title={t('common:notSufficientPermissions')}
                  disableHoverListener={allowed}
                  disableFocusListener={allowed}>
                  <span>
                    <Button
                      sx={{ ml: 2 }}
                      onClick={() =>
                        navigate({
                          to: '/tenant/spaces/floor-planner',
                          state: { backUrl: location.href },
                        })
                      }
                      color="primary"
                      disabled={!allowed}
                      startIcon={<Plus fontSize="small" />}
                      variant="outlinedContained"
                      size="large">
                      {t('spaces:common.mapSpace')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Can> */}

            <Can I="upload" a="Spaces" passThrough>
              {allowed => (
                <Tooltip
                  title={t('common:notSufficientPermissions')}
                  disableHoverListener={allowed}
                  disableFocusListener={allowed}>
                  <span>
                    <Button
                      sx={{ ml: 2 }}
                      onClick={handleOpenUploadSpacesDialog}
                      color="primary"
                      disabled={!allowed}
                      startIcon={<Plus fontSize="small" />}
                      variant="contained"
                      size="large">
                      {t('spaces:common.uploadSpaces')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Can>
          </>
        }
      />

      <HintBox>
        <Typography variant="body2" color="textSecondary">
          {overviewHint}
        </Typography>
      </HintBox>

      <Page.ContentContainer>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            px: 3,
            py: 2.5,
          }}>
          <Query
            searchAsType
            disabled={false}
            onChange={handleChangeQuery}
            sx={{
              minWidth: 200,
            }}
            value={query}
          />

          <SpaceStateFilterPopover
            filters={filter}
            handleChangeFilterValue={handleChangeFilterValue}
          />

          <SpacesMoreFilters
            anchor={'right'}
            filters={filter}
            handleChangeFilterValue={handleChangeFilterValue}
            resetFilters={resetFilters}
          />
        </Box>

        <SpacesFilterChips
          filters={filter}
          handleChangeFilterValue={handleChangeFilterValue}
        />

        <SpacesTable
          data={results?.hits ?? []}
          isLoading={isLoading}
          error={error?.response?.data?.message}
          sortController={sortController}
          setSortController={setSortController}
          handleChangeSort={handleChangeSort}
          actionsSlot={actionsSlot}
        />

        <Divider sx={{ mt: 'auto' }} />

        <Pagination
          disabled={isLoading}
          isFetching={isFetching}
          changePage={handleChangePage}
          rowsCount={results?.estimatedTotalHits || 0}
          page={page}
          perPage={perPage}
          totalPages={totalPages}
          handlePerPageChange={handleChangeLimit}
        />
      </Page.ContentContainer>

      <UploadSpacesDialog
        open={isOpenUploadSpacesDialog}
        onCancel={handleCloseUploadSpacesDialog}
      />

      <DeleteSpaceDialog
        open={isOpenDeleteSpacesDialog}
        onCancel={() => {
          handleCloseDeleteSpacesDialog();
          setSelectedSpace(null);
        }}
        spaceToken={selectedSpace?.token ?? ''}
        spaceGroupToken={selectedSpace?.spaceGroupToken ?? ''}
      />

      <BlockSpaceDialog
        open={isOpenBlockSpaceDialog}
        onCancel={handleCloseBlockSpaceDialog}
        spaceToken={selectedSpace?.token ?? ''}
        spaceBlockedStates={selectedSpace?.spaceBlockedStates ?? []}
      />

      <UnblockSpaceDialog
        open={isOpenUnblockSpaceDialog}
        onCancel={handleCloseUnblockSpaceDialog}
        spaceToken={selectedSpace?.token ?? ''}
        spaceBlockedStates={selectedSpace?.spaceBlockedStates ?? []}
      />
    </>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'Spaces'),
  Spaces,
);
